import {
  MolsonBox,
  MolsonButton,
  MolsonIcon,
  MolsonTypography
} from '@components/reusable'
import type { SxProps } from '@mui/material'
import type { ReactNode } from 'react'
import { molsonUseTheme } from '@/theme/customMuiExports'

export enum BannerType {
  Error = 'Error',
  Success = 'Success',
  Warning = 'Warning',
  Alert = 'Alert'
}

export type BannerTypeKeys = keyof typeof BannerType

type BannerTypeMapping = {
  [K in BannerTypeKeys]?: string
}

export type BannerProps = {
  buttonText?: string
  customSx?: SxProps
  handleBannerAction?: () => void
  primaryText?: string
  reviseOrUpdate?: boolean
  secondaryText?: string
  customSecondaryText?: string | ReactNode
  bannerType: BannerType | null
  stockIssue?: {
    findReplacementBtnText: string
    handleFindReplacement: () => void
    handleRemove: () => void
    removeBtnText: string
  } | null
  isItemArchived?: boolean
  handleRemoveArchivedItem?: () => void
  isNonEditableQuote?: boolean
  soldPendingDeliveryWithPriceChanges?: boolean
}

export const Banner = ({
  isItemArchived,
  buttonText = '',
  customSx,
  handleBannerAction,
  primaryText,
  reviseOrUpdate = false,
  secondaryText,
  customSecondaryText,
  bannerType,
  stockIssue,
  handleRemoveArchivedItem,
  isNonEditableQuote = false,
  soldPendingDeliveryWithPriceChanges
}: BannerProps) => {
  const theme = molsonUseTheme()

  const displayReviseOrUpdateBtn =
    reviseOrUpdate && buttonText && !stockIssue && !isItemArchived

  const backgroundColorObj: BannerTypeMapping = {
    Warning: theme.palette.primary.warningBannerBg,
    Success: theme.palette.secondary.surfaceSuccess,
    Error: theme.palette.primary.errorBannerBg,
    Alert: theme.palette.secondary.lightBlue
  }

  const contentColorObj: BannerTypeMapping = {
    Warning: theme.palette.primary.warningContent,
    Success: theme.palette.primary.successContentSecondary,
    Error: theme.palette.primary.errorContent,
    Alert: theme.palette.secondary.darkBlue
  }

  const bannerIconObj = {
    Warning: <MolsonIcon variant="WARNING" iconColor="warning" />,
    Success: <MolsonIcon variant="CHECK_CIRCLE_OUTLINE" iconColor="success" />,
    Error: <MolsonIcon variant="ERROR_OUTLINE" iconColor="error" />,
    Alert: <MolsonIcon variant="INFO_OUTLINE" iconColor="info" />
  }

  const bannerIcon = () => {
    if (soldPendingDeliveryWithPriceChanges) {
      return bannerIconObj.Warning
    }
    return bannerType ? bannerIconObj[bannerType] : null
  }

  const renderButtonsSection = () => {
    if (isItemArchived) {
      return (
        <MolsonButton
          color="error"
          onClick={handleRemoveArchivedItem}
          sx={{ minWidth: 'fit-content' }}
          variant="contained"
          disabled={isNonEditableQuote}
        >
          {buttonText}
        </MolsonButton>
      )
    } else if (stockIssue) {
      return (
        <MolsonBox display="flex" gap={1}>
          <MolsonButton
            sx={{
              fontSize: {
                lg: '16px',
                sm: '12px'
              }
            }}
            color="secondary"
            onClick={stockIssue.handleFindReplacement}
            variant="outlined"
          >
            {stockIssue.findReplacementBtnText}
          </MolsonButton>
          <MolsonButton
            sx={{
              fontSize: {
                lg: '16px',
                sm: '12px'
              }
            }}
            color="error"
            onClick={stockIssue.handleRemove}
            variant="contained"
          >
            {stockIssue.removeBtnText}
          </MolsonButton>
        </MolsonBox>
      )
    } else {
      return null
    }
  }

  return (
    <MolsonBox
      sx={{
        alignItems: 'center',
        backgroundColor: bannerType
          ? backgroundColorObj[bannerType]
          : undefined,
        borderRadius: '8px',
        display: 'flex',
        gap: 1,
        justifyContent: 'space-between',
        mt: 2,
        padding: 2,
        position: 'relative',
        width: '100%',
        zIndex: 2,
        ...customSx
      }}
      boxShadowType={reviseOrUpdate ? 'none' : 'default'}
    >
      <MolsonBox
        alignItems={secondaryText ? 'flex-start' : 'center'}
        display="flex"
        gap={1}
      >
        {bannerIcon()}

        <MolsonBox sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
          {primaryText ? (
            <MolsonTypography
              color={bannerType ? contentColorObj[bannerType] : undefined}
              fontWeight={700}
            >
              {primaryText}
            </MolsonTypography>
          ) : null}
          {secondaryText || customSecondaryText ? (
            <MolsonTypography
              color={bannerType ? contentColorObj[bannerType] : undefined}
              variant="body2"
            >
              {secondaryText ?? customSecondaryText}
            </MolsonTypography>
          ) : null}
        </MolsonBox>
      </MolsonBox>

      {displayReviseOrUpdateBtn ? (
        <MolsonButton
          color="secondary"
          onClick={handleBannerAction}
          sx={{ height: 36, minWidth: 'fit-content' }}
          variant="outlined"
        >
          {buttonText}
        </MolsonButton>
      ) : null}
      {renderButtonsSection()}
    </MolsonBox>
  )
}
